import { Box, Spinner } from "@reflecto/client-ui";
import { useEffect, useRef, useState } from "react";
import { FeedbackModal } from "./components/FeedbackModal";
import { useNotificationsStream } from "./hooks/useNotificationsStream";
import { useStorage } from "./hooks/useStorage";
import { messages } from "./i18n";
import { applyBadge } from "./lib/badge";
import { isShareTargetPath, useRoute } from "./lib/router";
import { applyTheme } from "./lib/theme";
import { MainPage } from "./pages/MainPage";
import { PairingPage } from "./pages/PairingPage";
import { SettingsPage } from "./pages/SettingsPage";
import { ShareTargetPage } from "./pages/ShareTargetPage";

/**
 * Top-level router. Gates on the `paired` storage flag: if the
 * browser is paired with an Android device we land on MainPage /
 * SettingsPage (route-driven); otherwise PairingPage drives the
 * 6-digit code flow.
 *
 * `paired` alone is ambiguous (unset vs in-flight both read as
 * `undefined`), so we also consume `hydrated` — it flips true the
 * moment the first IndexedDB read for this specific key settles,
 * after the `value` state has already been updated. The router
 * renders a spinner until then.
 *
 * Hash routing (`useRoute`) decides which paired-side page to show.
 * The SW deep-links to `#/c/:conversationKey` from the
 * `notificationclick` handler — that route falls through to MainPage,
 * which reads the key (via `useRoute`) and scrolls to + highlights the
 * conversation through `SidePanelNotificationList`'s `scrollTo` prop.
 * `#/settings` shows SettingsPage.
 */
export function App() {
  const { value: paired, hydrated } = useStorage("paired");
  const { value: theme } = useStorage("theme");
  const { value: unreadCount } = useStorage("unreadCount");
  const route = useRoute();
  const [showFeedback, setShowFeedback] = useState(false);
  // Tracks in-flight POST so the route-change effect below doesn't unmount the modal mid-submit.
  const feedbackSendingRef = useRef(false);
  // Must read the LIVE route — a ref keeps it current so a settle after navigating away still closes the modal.
  const routeNameRef = useRef(route.name);
  useEffect(() => {
    routeNameRef.current = route.name;
  }, [route.name]);

  // SSE lifecycle is owned at the router level so it survives
  // MainPage ↔ SettingsPage navigation. Mounting on MainPage caused
  // the cleanup to fire (and write `sseConnected: false`) every time
  // the user opened settings, which the SettingsPanel then rendered
  // as a permanent "Reconnecting…" status.
  //
  // Suppressed on the share-target path: a cold share-sheet open lands
  // on ShareTargetPage (early return below), and we must not open the
  // SSE stream behind it.
  useNotificationsStream(paired === true && !isShareTargetPath());

  // Apply theme on every storage change. The first call from
  // undefined → "auto" wires up the system-preference listener
  // inside `applyTheme` for free.
  useEffect(() => {
    applyTheme(theme ?? "auto");
  }, [theme]);

  // Drive the title prefix + favicon canvas + setAppBadge whenever
  // the unread count changes. Coalesce undefined → 0 so a fresh
  // pair (storage hasn't initialised the counter yet) renders the
  // base title rather than `(undefined) Reflecto`.
  useEffect(() => {
    applyBadge(unreadCount ?? 0);
  }, [unreadCount]);

  // Close the modal on leaving settings unless a submit is in flight (keeps success/error setState alive).
  useEffect(() => {
    if (route.name !== "settings" && !feedbackSendingRef.current) setShowFeedback(false);
  }, [route.name]);

  // Web Share Target landing (`${BASE_URL}share-target`): the OS share
  // sheet opens the PWA at this real path with the shared text in the
  // query string. Detected here BEFORE the hydration + paired gate —
  // ShareTargetPage owns its own `hydrated` guard and handles the
  // unpaired case itself, confirming before sending. Path-based (not
  // reactive); a manual hash route is never used to reach it.
  //
  // NOTE: this early return does NOT suppress the SSE stream — the
  // `useNotificationsStream` call above runs every render, before this
  // line. The stream is kept closed on this path by the
  // `!isShareTargetPath()` argument on that call. Keep that guard if you
  // ever move or remove this early return.
  if (isShareTargetPath()) return <ShareTargetPage />;

  if (!hydrated) {
    return (
      <Box align="center" justify="center" paddingY="xl">
        <Spinner size="lg" tone="brand" label={messages.common.loading} />
      </Box>
    );
  }

  if (!paired) return <PairingPage />;

  // `route.name === "conversation"` falls through to MainPage, which
  // reads the key via `useRoute()` and scrolls to + highlights it.
  const page =
    route.name === "settings" ? (
      <SettingsPage onSendFeedback={() => setShowFeedback(true)} />
    ) : (
      <MainPage />
    );

  return (
    <>
      {/* inert removes the background from the a11y tree + tab order while the modal is open (aria-modal alone doesn't); display:contents adds no layout box. */}
      <div style={{ display: "contents" }} inert={showFeedback}>
        {page}
      </div>
      {showFeedback && (
        <FeedbackModal
          onClose={() => setShowFeedback(false)}
          onSendingChange={(s) => {
            feedbackSendingRef.current = s;
            // Submit settled while already off settings: close now (the route effect can't re-fire on a ref).
            // Read the live route via the ref — this callback may have been created at a render when
            // route.name was still "settings", so the captured value would wrongly keep the modal open.
            if (!s && routeNameRef.current !== "settings") setShowFeedback(false);
          }}
        />
      )}
    </>
  );
}
